import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  workspaceLocalCacheScopeKey,
  WorkspaceLocalCacheService,
  type WorkspaceLocalCacheWriteOptions,
} from './workspace-local-cache';

interface WorkspaceLocalCacheSnapshot<T> {
  hydrated: boolean;
  key: string | null;
  value: T | undefined;
}

export interface UseWorkspaceLocalCacheInput<
  T,
> extends WorkspaceLocalCacheWriteOptions {
  enabled?: boolean;
  namespace: string;
  segment: string;
  value: T | undefined;
}

export interface UseWorkspaceLocalCacheResult<T> {
  cachedValue: T | undefined;
  isHydrated: boolean;
}

export function useWorkspaceLocalCache<T>({
  enabled = true,
  maxEntries,
  maxSerializedChars,
  namespace,
  segment,
  value,
}: UseWorkspaceLocalCacheInput<T>): UseWorkspaceLocalCacheResult<T> {
  const cache = useService(WorkspaceLocalCacheService);
  const scope = useLiveData(cache.scope$);
  const scopeKey = workspaceLocalCacheScopeKey(scope);
  const requestKey = useMemo(
    () =>
      enabled && scopeKey
        ? `${scopeKey}:${encodeURIComponent(namespace)}:${encodeURIComponent(segment)}`
        : null,
    [enabled, namespace, scopeKey, segment]
  );
  const [snapshot, setSnapshot] = useState<WorkspaceLocalCacheSnapshot<T>>({
    hydrated: requestKey === null,
    key: requestKey,
    value: undefined,
  });
  const writeBaselineRef = useRef<{ key: string | null; value: T | undefined }>(
    {
      key: requestKey,
      value: undefined,
    }
  );
  const lastScopedRequestKeyRef = useRef<string | null>(requestKey);

  const isHydrated =
    requestKey === null || (snapshot.key === requestKey && snapshot.hydrated);
  const cachedValue = snapshot.key === requestKey ? snapshot.value : undefined;

  useEffect(() => {
    const changedAccountScope = Boolean(
      requestKey &&
      lastScopedRequestKeyRef.current &&
      lastScopedRequestKeyRef.current !== requestKey
    );
    writeBaselineRef.current = {
      key: requestKey,
      value: changedAccountScope ? value : undefined,
    };
    if (requestKey) {
      lastScopedRequestKeyRef.current = requestKey;
    }
    if (!requestKey) {
      setSnapshot({ hydrated: true, key: null, value: undefined });
      return;
    }
    let cancelled = false;
    setSnapshot({ hydrated: false, key: requestKey, value: undefined });
    void cache.read<T>(namespace, segment).then(cached => {
      if (!cancelled) {
        setSnapshot({ hydrated: true, key: requestKey, value: cached });
      }
    });
    return () => {
      cancelled = true;
    };
    // `value` is intentionally captured only when the cache identity changes.
    // A stale value from the previous account must never seed the new scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache, namespace, requestKey, segment]);

  useEffect(() => {
    if (
      !requestKey ||
      !isHydrated ||
      value === undefined ||
      (writeBaselineRef.current.key === requestKey &&
        writeBaselineRef.current.value === value)
    ) {
      return;
    }
    setSnapshot({ hydrated: true, key: requestKey, value });
    void cache.write(namespace, segment, value, {
      maxEntries,
      maxSerializedChars,
    });
  }, [
    cache,
    isHydrated,
    maxEntries,
    maxSerializedChars,
    namespace,
    requestKey,
    segment,
    value,
  ]);

  return { cachedValue, isHydrated };
}
